import { useEffect, useState } from 'react';
import {
  defaultRoute,
  findRouteByPath,
  getAccountantEntryPath,
  getNavigationItemsByRole,
  normalizeRoutePath,
} from './app/routes';
import {
  initializeBusinessContextFromUrl,
  getPortalRole,
  getAccountantSeaweedAccessTabs,
  hasPortalSession,
  isRouteAllowedForPortalRole,
} from './app/businessContext';
import CashAdvancePage from './pages/CashAdvancePage';
import CustomerPage from './pages/CustomerPage';
import DailyEntriesPage from './pages/DailyEntriesPage';
import DashboardPage from './pages/DashboardPage';
import DumpingPage from './pages/DumpingPage';
import ExpensesPage from './pages/ExpensesPage';
import InventoryPage from './pages/InventoryPage';
import LansaPage from './pages/LansaPage';
import ModulePlaceholderPage from './pages/ModulePlaceholderPage';
import PurchaseOrderPage from './pages/PurchaseOrderPage';
import ReleasingPage from './pages/ReleasingPage';
import SalesAnalyticsPage from './pages/SalesAnalyticsPage';
import SalesOrderPage from './pages/SalesOrderPage';
import StaffDashboardPage from './pages/StaffDashboardPage';
import StaffReceivingPage from './pages/StaffReceivingPage';
import SupplierSavingsPage from './pages/SupplierSavingsPage';
import SupplierPage from './pages/SupplierPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import LoginPage from './pages/LoginPage';
import { salesAnalytics, summaryCards } from './data/dashboardData';

function App() {
  useState(initializeBusinessContextFromUrl);
  const [currentPath, setCurrentPath] = useState(getInitialPath);
  const [currentSearch, setCurrentSearch] = useState(() => window.location.search);
  const activeRoute = resolveRoute(currentPath);
  const portalRole = getPortalRole();
  const navigationRole =
    portalRole === 'admin-custom' || portalRole === 'accountant' ? portalRole : activeRoute.role;
  const accountantSeaweedAccessTabs = getAccountantSeaweedAccessTabs();
  const navigationItems = getNavigationItemsByRole(navigationRole, accountantSeaweedAccessTabs);
  const isAuthorizedRoute = isRouteAllowedForPortalRole(activeRoute);

  useEffect(() => {
    if (window.location.pathname === '/' && !hasPortalSession()) {
      window.history.replaceState({}, '', '/login');
      setCurrentPath('/login');
      setCurrentSearch('');
      return undefined;
    }

    if (window.location.pathname === '/login') {
      setCurrentPath('/login');
      setCurrentSearch(window.location.search);
      return undefined;
    }

    const normalizedPath = resolveRoute(window.location.pathname).path;
    const sanitizedParams = new URLSearchParams(window.location.search);
    sanitizedParams.delete('portalToken');
    const sanitizedSearch = sanitizedParams.toString();
    const nextUrl = `${normalizedPath}${sanitizedSearch ? `?${sanitizedSearch}` : ''}`;

    if (nextUrl !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState({}, '', nextUrl);
    }

    setCurrentPath(normalizedPath);
    setCurrentSearch(sanitizedSearch ? `?${sanitizedSearch}` : '');

    const handlePopState = () => {
      setCurrentPath(resolveRoute(window.location.pathname).path);
      setCurrentSearch(window.location.search);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  if (currentPath === '/login') {
    return <LoginPage />;
  }

  function handleNavigate(nextPath) {
    const nextUrl = new URL(nextPath, window.location.origin);
    const normalizedPath = resolveRoute(nextUrl.pathname).path;
    const normalizedSearch = nextUrl.search;

    if (normalizedPath === currentPath && normalizedSearch === currentSearch) {
      return;
    }

    window.history.pushState({}, '', `${normalizedPath}${normalizedSearch}`);
    setCurrentPath(normalizedPath);
    setCurrentSearch(normalizedSearch);
  }

  if (!isAuthorizedRoute) {
    return <UnauthorizedPage />;
  }

  if (activeRoute.id === 'supplier') {
    return (
      <SupplierPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'lansa') {
    return (
      <LansaPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'customer') {
    return (
      <CustomerPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'purchase-order') {
    return (
      <PurchaseOrderPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'supplier-balancing') {
    return (
      <PurchaseOrderPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        lockedView="supplier-balancing"
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'initial-summary-po') {
    return (
      <PurchaseOrderPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        lockedView="supplier-batch"
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'sales-order') {
    return (
      <SalesOrderPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.role === 'admin' && activeRoute.id === 'releasing') {
    return (
      <ReleasingPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.role === 'admin' && activeRoute.id === 'sales-analytics') {
    return (
      <SalesAnalyticsPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'dumping') {
    return (
      <DumpingPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'inventory') {
    return (
      <InventoryPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'cash-advance') {
    return (
      <CashAdvancePage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        activeSearch={currentSearch}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'supplier-savings') {
    return (
      <SupplierSavingsPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'daily-entries') {
    return (
      <DailyEntriesPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.id === 'expenses') {
    return (
      <ExpensesPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.role === 'staff' && activeRoute.id === 'overview') {
    return (
      <StaffDashboardPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.role === 'staff' && activeRoute.id === 'receiving') {
    return (
      <StaffReceivingPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
      />
    );
  }

  if (activeRoute.role === 'staff') {
    return (
      <ModulePlaceholderPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
        title={activeRoute.label}
        description="This staff section will follow the same Seaweeds UI language with a staff-specific workflow."
      />
    );
  }

  if (activeRoute.id !== 'overview') {
    return (
      <ModulePlaceholderPage
        navigationItems={navigationItems}
        activePath={activeRoute.path}
        onNavigate={handleNavigate}
        title={activeRoute.label}
        description="This section will follow the same design system and shared layout as Supplier."
      />
    );
  }

  return (
    <DashboardPage
      navigationItems={navigationItems}
      activePath={activeRoute.path}
      onNavigate={handleNavigate}
      summaryCards={summaryCards}
      salesAnalytics={salesAnalytics}
    />
  );
}

function getInitialPath() {
  if (window.location.pathname === '/login') {
    return '/login';
  }

  return resolveRoute(window.location.pathname).path;
}

function resolveRoute(pathname) {
  const matchedRoute = findRouteByPath(normalizeRoutePath(pathname));

  if (matchedRoute.redirectTo) {
    if (getPortalRole() === 'accountant') {
      return findRouteByPath(getAccountantEntryPath(getAccountantSeaweedAccessTabs()));
    }

    return findRouteByPath(matchedRoute.redirectTo);
  }

  return matchedRoute || defaultRoute;
}

export default App;
